import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';

import {
  TABLE, THEAD, TBODY, TH, TR, TD,
} from 'zignal-landscape/lib/Table';
import themeable from 'zignal-landscape/lib/themeable';

// Per-row actions dropdown matching Account Settings → Workspaces. Uses the
// global SCSS classes from sass/partials/_dropdown-table-actions.scss
// (table-actions / actions-toggle / actions-dropdown / actions-icon).
const RowActionsMenu = ({ onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) {return undefined;}
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {setOpen(false);}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const choose = (fn) => (e) => {
    e.preventDefault();
    setOpen(false);
    fn();
  };

  return (
    <div className="actions-dropdown-wrapper abbreviated-layout" ref={wrapperRef}>
      <div className="table-actions">
        <a
          className="actions-toggle btn btn-mini"
          onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        >
          actions <i className="icon-caret-down" />
        </a>
        <ul className="actions-dropdown" style={open ? { display: 'block' } : undefined}>
          <li>
            <a className="actions" onClick={choose(onEdit)} title="EDIT DEFAULT LOCATION">
              <i className="actions-icon icon-pencil" />
              <span className="actions-label">EDIT DEFAULT LOCATION</span>
            </a>
          </li>
          <li>
            <a className="actions" onClick={choose(onDelete)} title="DELETE DEFAULT LOCATION">
              <i className="actions-icon icon-trash" />
              <span className="actions-label">DELETE DEFAULT LOCATION</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};
RowActionsMenu.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const formatDecimal = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) {return '';}
  return Number(n).toFixed(6);
};

// Each column: id, header, accessor, sortValue (defaults to accessor), sortable.
const COLUMNS = [
  { id: 'term',        label: 'Term',        accessor: (r) => r.term },
  { id: 'platform',    label: 'Platform',    accessor: (r) => r.platform },
  { id: 'field',       label: 'Field',       accessor: (r) => r.story_field },
  { id: 'location',    label: 'Location',    accessor: (r) => r.location && r.location.location },
  { id: 'latitude',    label: 'Latitude',    accessor: (r) => r.location && r.location.latlong && r.location.latlong.lat,
    render: (r) => formatDecimal(r.location && r.location.latlong && r.location.latlong.lat) },
  { id: 'longitude',   label: 'Longitude',   accessor: (r) => r.location && r.location.latlong && r.location.latlong.lon,
    render: (r) => formatDecimal(r.location && r.location.latlong && r.location.latlong.lon) },
  { id: 'resolution',  label: 'Resolution',  accessor: (r) => r.location && r.location.resolution },
  { id: 'country_iso', label: 'CountryISO',
    accessor: (r) => r.location && r.location.country_iso && String(r.location.country_iso).toUpperCase() },
  { id: 'country',     label: 'Country',     accessor: (r) => r.location && r.location.country },
];

const isEmpty = (v) => v === null || v === undefined || v === '';

const compareValues = (a, b) => {
  if (a === b) {return 0;}
  if (typeof a === 'number' && typeof b === 'number') {return a - b;}
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const SortIndicator = ({ direction }) => {
  // Show both arrows; highlight the active one.
  const dim = { color: '#bbb' };
  const lit = { color: '#333' };
  return (
    <span style={{display: 'inline-flex', flexDirection: 'column', marginLeft: '4px', fontSize: '0.6em', lineHeight: 1}}>
      <span style={direction === 'asc' ? lit : dim}>▲</span>
      <span style={direction === 'desc' ? lit : dim}>▼</span>
    </span>
  );
};
SortIndicator.propTypes = { direction: PropTypes.oneOf(['asc', 'desc', null]) };

const DefaultLocationsTable = ({
  styles,
  locations = [],
  onEdit,
  onDelete,
}) => {
  // sortBy: { columnId, direction: 'asc' | 'desc' } or null (no sort)
  const [sortBy, setSortBy] = useState(null);

  const handleHeaderClick = (columnId) => {
    setSortBy((current) => {
      if (!current || current.columnId !== columnId) {return { columnId, direction: 'asc' };}
      if (current.direction === 'asc') {return { columnId, direction: 'desc' };}
      return null; // third click clears sort
    });
  };

  const sortedLocations = useMemo(() => {
    if (!sortBy) {return locations;}
    const column = COLUMNS.find((c) => c.id === sortBy.columnId);
    if (!column) {return locations;}
    const factor = sortBy.direction === 'desc' ? -1 : 1;
    return [...locations].sort((a, b) => {
      const va = column.accessor(a);
      const vb = column.accessor(b);
      // Empties always sink to the bottom regardless of direction.
      if (isEmpty(va) && isEmpty(vb)) {return 0;}
      if (isEmpty(va)) {return 1;}
      if (isEmpty(vb)) {return -1;}
      return factor * compareValues(va, vb);
    });
  }, [locations, sortBy]);

  const locationRows = map(sortedLocations, (item) => (
    <TR key={item._id}>
      {COLUMNS.map((col) => (
        <TD key={col.id}>{col.render ? col.render(item) : col.accessor(item)}</TD>
      ))}
      <TD className={styles.actionsCell}>
        <RowActionsMenu
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
        />
      </TD>
    </TR>
  ));

  return (
    <div className={styles.DefaultLocationsTableWrapper}>
      <TABLE>
        <THEAD>
          <TR>
            {COLUMNS.map((col) => {
              const isActive = sortBy && sortBy.columnId === col.id;
              const direction = isActive ? sortBy.direction : null;
              return (
                <TH key={col.id}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderClick(col.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {handleHeaderClick(col.id);} }}
                    style={{cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center'}}
                  >
                    {col.label}
                    <SortIndicator direction={direction} />
                  </span>
                </TH>
              );
            })}
            <TH>Actions</TH>
          </TR>
        </THEAD>
        <TBODY>
          {locationRows}
        </TBODY>
      </TABLE>
    </div>
  );
};

DefaultLocationsTable.propTypes = {
  styles: PropTypes.object.isRequired,
  locations: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default themeable(DefaultLocationsTable);
